import {
  CATEGORY_OPTIONS,
  DEFAULT_CONTACT_LIST_CATEGORY,
  isContactListCategory,
  isContactListSort,
  SORT_OPTIONS,
} from "@/components/contacts/ContactFilters.tsx";
import {
  type ContactListFilters,
  ContactList,
  DEFAULT_CONTACT_LIST_FILTERS,
} from "@/components/contacts/ContactList.tsx";
import { getConnectors } from "@/components/connectors/api.ts";
import { LocalCanvasHost } from "@/components/canvas/LocalCanvasHost.tsx";
import { ContactDetailBody } from "@/components/contacts/ContactDetailSidebar.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import { useContact } from "@/hooks/useContacts.tsx";
import { DEFAULT_CONTACT_LIST_SORT } from "@/types/contact.ts";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { Redirect, useLocation, useParams, useSearchParams } from "wouter";
import { getThreadParticipantsPath } from "@/components/threads/util.ts";
import { AddContactDialog } from "../components/contacts/AddContactDialog.tsx";
import { CONTACTS_BASE_PATH } from "./ContactList.tsx";
import {
  appliedFilterLabel,
  type PanelControlDimension,
  PanelControls,
} from "./Settings/panelControls.tsx";
import { listQuerySuffix } from "./Settings/settingsNav.ts";
import { panelDescriptionClass, SettingsPanelHeader } from "./Settings/SettingsPanelHeader.tsx";
import { CanvasPivotPanel } from "@/components/layout/SplitPanel/CanvasPivotPanel.tsx";
import { PanelToolbar } from "@/components/layout/SplitPanel/PanelToolbar.tsx";

/** Parses a numeric query param, treating malformed values as absent. */
const numericParam = (value: string | null): number | undefined => {
  if (!value) return undefined;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * A contact opened from the settings list, as a drill-down child screen: the
 * breadcrumb (Contacts › name) on desktop, the contact's name in the chrome
 * bar on mobile, and the same shared detail body every other surface renders.
 */
const ContactDrillDown = ({ contactId, onBack }: { contactId: string; onBack: () => void }) => {
  const { contact, isLoading } = useContact(contactId);

  let body: ReactNode;
  if (isLoading) {
    body = <Loader className="py-8" />;
  } else if (!contact) {
    body = <p className="p-4 text-sm text-muted-foreground">Contact not found.</p>;
  } else {
    body = <ContactDetailBody contactId={contact.id} onDeleted={onBack} />;
  }

  // No canvas of its own, unlike every other host of this body. A settings tab
  // is not a panel — it is content some way down the settings screen's single
  // scroller, so there is no panel-sized box here to anchor a card against; one
  // mounted here would be as tall as the contact and would scroll away with it.
  // An activity row on this surface keeps loading the entity's own page, and
  // browser back returns to the contact.
  return (
    <div className="pb-12">
      <SettingsPanelHeader
        title={contact?.name || "Contact"}
        parentLabel="People"
        onBack={onBack}
        backStatsigName="contacts-panel-back"
      />
      {body}
    </div>
  );
};

/**
 * The user's contacts, rendered as a Settings tab. The header row carries the
 * whole toolkit — search, the category/sort/account pickers, and Add contact —
 * while the list below only reads the resulting filters. A `:subId` in the
 * route is an open contact, rendered as a drill-down child screen.
 *
 * Filter state lives in the URL rather than component state so the deep links
 * the legacy `/contacts` route redirects here (`contactId`, `connectorId`, `q`)
 * keep resolving to the same list. A legacy `threadId` scope is forwarded to the
 * thread's own participants route, which owns that list now.
 */
/**
 * Encodes "no account scope" for the account axis, which speaks in strings so
 * every axis is URL-serializable. The connector id is the one non-string
 * domain, encoded here at the edge exactly as it round-trips through the query
 * param.
 */
const ALL_CONNECTORS = "all";

/**
 * The contact list's category, account, and sort axes, owned in one place so
 * the settings page and the canvas view can't drift into offering different
 * ones — the same shape `useLibraryControls` and `useRoutinesListControls`
 * return, for the same reason.
 *
 * The filters themselves stay with the caller, because the two hosts hold them
 * differently: settings keeps them in the URL so a filtered list can be linked
 * to, and the canvas keeps them in component state, having no URL of its own to
 * carry them. What must not differ is the set of questions the two ask, and
 * that is what this owns.
 */
export const useContactListControls = ({
  filters,
  onChange,
  statsigPrefix,
  enabled = true,
}: {
  filters: ContactListFilters;
  onChange: (patch: Partial<ContactListFilters>) => void;
  /** Prefixes each axis's Statsig name, so the two hosts stay tellable apart. */
  statsigPrefix: string;
  /** False where the tools are hidden, to skip the accounts fetch behind them. */
  enabled?: boolean;
}) => {
  // Deduped by react-query against ContactList's own connectors query. Only
  // accounts that actually hold contacts (i.e. have an email) belong in the
  // picker — MCP servers, Slack, etc. would render as blank options.
  const { data: connectorsData } = useQuery({
    queryKey: ["connectors"],
    queryFn: getConnectors,
    enabled,
  });
  const connectorOptions = (connectorsData?.connectors ?? []).filter((c) => c.email);

  // A `connectorId` naming an account that is disconnected, or never held
  // contacts, still scopes the list query — so the page would show an empty
  // list for a filter the user can't see. Drop it once the accounts are known.
  // Guarded on the loaded data, never on its absence: clearing while the query
  // is still in flight would discard a perfectly good scope on every reload.
  const scopeIsStale =
    connectorsData !== undefined &&
    filters.connectorId !== undefined &&
    !connectorOptions.some((connector) => connector.id === filters.connectorId);
  useEffect(() => {
    if (scopeIsStale) onChange({ connectorId: undefined });
  });

  // The shared control speaks in strings so every axis is URL-serializable;
  // the connector id is the one non-string domain, encoded here at the edge
  // exactly as it already round-trips through the query param.
  const contactAxes: PanelControlDimension[] = [
    {
      label: "Category",
      value: filters.category,
      onChange: (value) => {
        if (isContactListCategory(value)) onChange({ category: value });
      },
      options: CATEGORY_OPTIONS,
      defaultValue: DEFAULT_CONTACT_LIST_CATEGORY,
      display: "segmented",
      statsigName: `${statsigPrefix}-category`,
    },
  ];
  // A single account needs no picker; the list is unambiguously from it. But a
  // scope that IS set always gets one, even when it names a connector that is
  // gone or was never eligible: the query stays narrowed either way, so hiding
  // the axis would leave an empty list, a trigger claiming nothing is applied,
  // and no way out but editing the URL.
  if (connectorOptions.length > 1 || filters.connectorId !== undefined) {
    contactAxes.push({
      label: "Account",
      value: filters.connectorId?.toString() ?? ALL_CONNECTORS,
      onChange: (value) =>
        onChange({
          connectorId: value === ALL_CONNECTORS ? undefined : Number.parseInt(value, 10),
        }),
      options: [
        { value: ALL_CONNECTORS, label: "All accounts" },
        ...connectorOptions.map((connector) => ({
          value: connector.id.toString(),
          label: connector.email ?? connector.displayName,
        })),
      ],
      defaultValue: ALL_CONNECTORS,
      statsigName: `${statsigPrefix}-account`,
    });
  }

  // Both orderings are full views of the list, so a sort choice narrows
  // nothing and must not light the applied treatment. `PanelControls` stamps
  // the role that says so from the slot this is passed in.
  const sort: PanelControlDimension = {
    label: "Sort",
    value: filters.sort,
    onChange: (value) => {
      if (isContactListSort(value)) onChange({ sort: value });
    },
    options: SORT_OPTIONS,
    defaultValue: DEFAULT_CONTACT_LIST_SORT,
    statsigName: `${statsigPrefix}-sort`,
  };

  return { filters: contactAxes, sort };
};

export const ContactsPanel = () => {
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const [, setLocation] = useLocation();
  const params = useParams<{ subId?: string }>();

  // The query-param spelling is the legacy deep link; the path is canonical.
  const contactId = params.subId ?? searchParams.get("contactId") ?? undefined;
  const threadId = numericParam(searchParams.get("threadId"));

  const categoryParam = searchParams.get("category");
  const sortParam = searchParams.get("sort");
  const filters: ContactListFilters = {
    // `accountId` is the legacy spelling of this param, still present in links
    // that predate the connector pill — read it as a fallback so those keep working.
    connectorId:
      numericParam(searchParams.get("connectorId")) ?? numericParam(searchParams.get("accountId")),
    // An unrecognized value in a hand-edited or stale URL falls back to the
    // default rather than rendering a list with no pill selected.
    category: isContactListCategory(categoryParam) ? categoryParam : DEFAULT_CONTACT_LIST_CATEGORY,
    searchQuery: searchParams.get("q") ?? "",
    sort: isContactListSort(sortParam) ? sortParam : DEFAULT_CONTACT_LIST_SORT,
  };

  const handleFiltersChange = useCallback(
    (next: ContactListFilters) => {
      const params = new URLSearchParams();
      // Preserve the deep-link scope; only the filters are being changed.
      if (contactId) params.set("contactId", contactId);
      if (next.connectorId !== undefined) params.set("connectorId", next.connectorId.toString());
      // Defaults are omitted so a URL with no filter params is the canonical
      // "unfiltered list" link rather than one spelling of several.
      if (next.category !== DEFAULT_CONTACT_LIST_FILTERS.category) {
        params.set("category", next.category);
      }
      if (next.sort !== DEFAULT_CONTACT_LIST_FILTERS.sort) {
        params.set("sort", next.sort);
      }
      if (next.searchQuery) params.set("q", next.searchQuery);
      const qs = params.toString();
      setLocation(`${CONTACTS_BASE_PATH}${qs ? `?${qs}` : ""}`, { replace: true });
    },
    [contactId, setLocation],
  );

  const updateFilters = (patch: Partial<ContactListFilters>) =>
    handleFiltersChange({ ...filters, ...patch });

  const listQuery = listQuerySuffix(searchParams);

  // Search and filters only make sense for the global "All contacts" list; a
  // deep link to one contact hides them. A thread scope never gets here — it
  // redirects below.
  const showTools = !contactId;

  const { filters: contactAxes, sort: contactSortAxis } = useContactListControls({
    filters,
    onChange: updateFilters,
    statsigPrefix: "contacts",
    // Search and filters only make sense for the global list, so a deep link to
    // one contact skips the accounts fetch that feeds the account axis.
    enabled: showTools,
  });

  // A thread's people are their own route now, so an old `?threadId=` link is
  // forwarded there rather than quietly widening to every contact. Placed after
  // every hook above so none of them is skipped on the redirecting render.
  if (threadId !== undefined) {
    return <Redirect replace to={getThreadParticipantsPath(threadId)} />;
  }

  // A contact opened from the settings list renders as the drill-down child
  // screen. Back replaces rather than pushes, so backing out collapses the
  // child's history entry. Thread scopes never reach here — they redirect above.
  if (contactId) {
    return (
      <ContactDrillDown
        contactId={contactId}
        onBack={() => setLocation(`${CONTACTS_BASE_PATH}${listQuery}`, { replace: true })}
      />
    );
  }

  return (
    <div className="pb-12">
      <SettingsPanelHeader
        title="People"
        search={
          showTools
            ? {
                value: filters.searchQuery,
                onChange: (searchQuery) => updateFilters({ searchQuery }),
                placeholder: "Search people",
                statsigName: "contacts-search",
              }
            : undefined
        }
        filters={
          showTools ? (
            <PanelControls
              labeled
              filters={contactAxes}
              sort={contactSortAxis}
              subject="contacts"
              statsigName="contacts-filter"
            />
          ) : undefined
        }
        mobileFilters={
          showTools ? (
            <PanelControls
              filters={contactAxes}
              sort={contactSortAxis}
              subject="contacts"
              statsigName="contacts-filter"
            />
          ) : undefined
        }
        // The trigger's label IS the tool cluster's width now, so a change to
        // it has to re-run the header's collapse measurement.
        toolsKey={appliedFilterLabel({ filters: contactAxes, sort: contactSortAxis })}
        action={{
          label: "Add contact",
          icon: Plus,
          onClick: () => setIsAddContactOpen(true),
          statsigName: "settings-contacts-add",
        }}
      />

      {showTools && (
        <p className={panelDescriptionClass}>
          The people Okthx knows from your accounts and conversations.
        </p>
      )}

      <AddContactDialog open={isAddContactOpen} onOpenChange={setIsAddContactOpen} />

      <ContactList
        contactId={contactId}
        filters={filters}
        onOpenContact={(id) => setLocation(`${CONTACTS_BASE_PATH}/${id}${listQuery}`)}
      />
    </div>
  );
};

/**
 * Contacts on the canvas, with the same search, filter, and sort tools the
 * settings contacts page offers — and the same name, because it is the same
 * surface.
 *
 * The filter is local rather than URL-synced the way settings' copy is: a link
 * into a filtered list is the settings page's job. The open contact is not —
 * `/contacts/:id` is this surface's own address, which is why it arrives as a
 * prop rather than being kept here. The axes themselves are the shared ones, so
 * the two surfaces can't offer different answers to the same question.
 */
export const ContactsCanvasView = ({
  accountSwitcher,
  leading,
  openContactId,
  onOpenContact,
}: {
  accountSwitcher?: ReactNode;
  leading?: ReactNode;
  /** The contact the URL names, or null for the list. */
  openContactId: string | null;
  /**
   * Drill into a contact, or leave one with null. The page owns the address, so
   * it navigates and hands the result back down — this view never decides where
   * a contact lives.
   */
  onOpenContact: (contactId: string | null) => void;
}) => {
  const [filters, setFilters] = useState<ContactListFilters>(DEFAULT_CONTACT_LIST_FILTERS);
  const update = (patch: Partial<ContactListFilters>) =>
    setFilters((prev: ContactListFilters) => ({ ...prev, ...patch }));
  const [isAddOpen, setIsAddOpen] = useState(false);
  const { contact } = useContact(openContactId ?? undefined);

  const { filters: contactAxes, sort: contactSortAxis } = useContactListControls({
    filters,
    onChange: update,
    statsigPrefix: "people-canvas",
  });

  // Drilled in: the child screen owns the header, so the list's toolbar goes away
  // with the list they act on, exactly as they do in settings.
  if (openContactId) {
    return (
      // Around the panel, not inside it: this panel shares the screen with the
      // canvas column, so without a canvas of its own an event opened here would
      // present over there — beside the contact it belongs to rather than over
      // it. Outside, because the panel's body is a scroller holding a
      // content-sized, width-capped column, and a card anchored in there would
      // be as tall as the contact rather than the panel, and would scroll away
      // with it.
      <LocalCanvasHost key={openContactId}>
        <CanvasPivotPanel
          title={contact?.name || "Contact"}
          parentLabel="People"
          onBack={() => onOpenContact(null)}
          backStatsigName="people-canvas-back"
          leading={leading}
          accountSwitcher={accountSwitcher}
        >
          {/* A deleted contact is not somewhere to stay, and leaving it is the
              host's move to make now that the way out is an address (BIG-6937). */}
          <ContactDetailBody contactId={openContactId} onDeleted={() => onOpenContact(null)} />
        </CanvasPivotPanel>
      </LocalCanvasHost>
    );
  }

  return (
    <CanvasPivotPanel
      title="People"
      leading={leading}
      accountSwitcher={accountSwitcher}
      toolbar={
        <PanelToolbar
          description="The people Okthx knows from your accounts and conversations."
          controls={{ filters: contactAxes, sort: contactSortAxis }}
          search={{
            value: filters.searchQuery,
            onChange: (searchQuery: string) => update({ searchQuery }),
            placeholder: "Search people",
            statsigName: "people-canvas-search",
          }}
          subject="contacts"
          statsigName="people-canvas-filter"
          action={{
            label: "Add contact",
            icon: Plus,
            onClick: () => setIsAddOpen(true),
            statsigName: "people-canvas-add",
          }}
        />
      }
    >
      {/* `ContactList` only offers the drill-down when a host asks for it —
          without `onOpenContact` it falls back to opening the contact somewhere
          else entirely, which on this surface meant an overlay landing on top
          of the canvas. */}
      <ContactList filters={filters} onOpenContact={onOpenContact} />
      <AddContactDialog open={isAddOpen} onOpenChange={setIsAddOpen} />
    </CanvasPivotPanel>
  );
};
